import { Network } from '../interfaces/network'
import { BICONOMY, PIMLICO } from './bundlers'

const networks: Network[] = [
  {
    name: 'Ethereum',
    nativeAssetSymbol: 'ETH',
    has7702: true,
    nativeAssetName: 'Ether',
    rpcUrls: [
      'https://apis.zkxwallet.com/invictus/ethereum',
      'https://ethereum-rpc.publicnode.com',
      'https://eth.drpc.org'
    ],
    selectedRpcUrl: 'https://apis.zkxwallet.com/invictus/ethereum',
    rpcNoStateOverride: false,
    chainId: 1n,
    explorerUrl: 'https://etherscan.io',
    erc4337: { enabled: false, hasPaymaster: true, hasBundlerSupport: true },
    // ZKX contracts deployed 2026-07-24 (AmbireAccount/Factory/7702 impl, EIP-2470).
    // hasRelayer stays false statically: the ZKX relayer flips it on per chain
    // via /v2/config/networks once its spend path is live — the static default
    // must be safe on its own (docs/gas-tank-frontend-integration.md §4).
    isSAEnabled: true,
    areContractsDeployed: true,
    hasRelayer: false,
    platformId: 'ethereum',
    nativeAssetId: 'ethereum',
    hasSingleton: true,
    features: [],
    feeOptions: { is1559: true },
    predefined: true,
    wrappedAddr: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
    disableEstimateGas: true
  },
  {
    name: 'Polygon',
    nativeAssetSymbol: 'POL',
    // ZKX contracts (AmbireAccount/Factory/7702 impl) are NOT deployed on this
    // chain — SA/7702 must stay off or users could fund an SA address that can
    // never be deployed here. See docs/gas-tank-frontend-integration.md §2.6.
    has7702: false,
    nativeAssetName: 'Polygon',
    rpcUrls: [
      'https://apis.zkxwallet.com/invictus/polygon',
      'https://polygon.drpc.org'
    ],
    selectedRpcUrl: 'https://apis.zkxwallet.com/invictus/polygon',
    rpcNoStateOverride: false,
    chainId: 137n,
    explorerUrl: 'https://polygonscan.com',
    erc4337: {
      enabled: false,
      hasPaymaster: true,
      hasBundlerSupport: true,
      bundlers: [PIMLICO, BICONOMY],
      defaultBundler: PIMLICO
    },
    isSAEnabled: false,
    areContractsDeployed: false,
    hasRelayer: false,
    platformId: 'polygon-pos',
    nativeAssetId: 'matic-network',
    hasSingleton: true,
    features: [],
    feeOptions: { is1559: false, feeIncrease: 10n },
    predefined: true,
    wrappedAddr: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270',
    oldNativeAssetSymbols: ['MATIC'],
    disableEstimateGas: true
  },
  {
    name: 'OP Mainnet',
    nativeAssetSymbol: 'ETH',
    // ZKX contracts NOT deployed here — see Polygon note / §2.6.
    has7702: false,
    nativeAssetName: 'Ether',
    rpcUrls: [
      'https://apis.zkxwallet.com/invictus/optimism',
      'https://optimism-rpc.publicnode.com',
      'https://mainnet.optimism.io'
    ],
    selectedRpcUrl: 'https://apis.zkxwallet.com/invictus/optimism',
    rpcNoStateOverride: false,
    chainId: 10n,
    explorerUrl: 'https://optimistic.etherscan.io',
    erc4337: {
      enabled: true,
      hasPaymaster: true,
      hasBundlerSupport: true,
      bundlers: [PIMLICO, BICONOMY],
      defaultBundler: BICONOMY,
      increasePreVerGas: 5
    },
    isSAEnabled: false,
    areContractsDeployed: false,
    hasRelayer: false,
    platformId: 'optimistic-ethereum',
    nativeAssetId: 'ethereum',
    hasSingleton: true,
    features: [],
    feeOptions: {
      is1559: true,
      elasticityMultiplier: 6n,
      baseFeeMaxChangeDenominator: 50n
    },
    isOptimistic: true,
    predefined: true,
    disableEstimateGas: true,
    wrappedAddr: '0x4200000000000000000000000000000000000006'
  },
  {
    name: 'Avalanche',
    nativeAssetSymbol: 'AVAX',
    has7702: false,
    nativeAssetName: 'Avalanche',
    rpcUrls: [
      'https://apis.zkxwallet.com/invictus/avalanche',
      'https://avalanche-c-chain-rpc.publicnode.com',
      'https://api.avax.network/ext/bc/C/rpc'
    ],
    selectedRpcUrl: 'https://apis.zkxwallet.com/invictus/avalanche',
    rpcNoStateOverride: false,
    chainId: 43114n,
    explorerUrl: 'https://snowtrace.io',
    erc4337: {
      enabled: true,
      hasPaymaster: true,
      hasBundlerSupport: true
    },
    // ZKX contracts NOT deployed here — see Polygon note / §2.6.
    isSAEnabled: false,
    areContractsDeployed: false,
    hasRelayer: false,
    platformId: 'avalanche',
    nativeAssetId: 'avalanche-2',
    hasSingleton: true,
    features: [],
    feeOptions: {
      is1559: true,
      minBaseFee: 25000000000n // 25 gwei
    },
    predefined: true,
    disableEstimateGas: true,
    wrappedAddr: '0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7'
  },
  {
    name: 'Arbitrum',
    nativeAssetSymbol: 'ETH',
    // ZKX contracts NOT deployed here — see Polygon note / §2.6.
    has7702: false,
    nativeAssetName: 'Ether',
    rpcUrls: [
      'https://apis.zkxwallet.com/invictus/arbitrum',
      'https://arbitrum-one-rpc.publicnode.com',
      'https://arb1.arbitrum.io/rpc'
    ],
    selectedRpcUrl: 'https://apis.zkxwallet.com/invictus/arbitrum',
    rpcNoStateOverride: false,
    chainId: 42161n,
    explorerUrl: 'https://arbiscan.io',
    erc4337: {
      enabled: true,
      hasPaymaster: true,
      hasBundlerSupport: true,
      bundlers: [PIMLICO, BICONOMY],
      defaultBundler: PIMLICO
    },
    isSAEnabled: false,
    areContractsDeployed: false,
    hasRelayer: false,
    platformId: 'arbitrum-one',
    nativeAssetId: 'ethereum',
    hasSingleton: true,
    features: [],
    feeOptions: {
      is1559: true,
      minBaseFee: 100000000n // 1 gwei
    },
    predefined: true,
    wrappedAddr: '0x82af49447d8a07e3bd95bd0d56f35241523fbab1'
  },
  {
    name: 'Base',
    nativeAssetSymbol: 'ETH',
    has7702: true,
    nativeAssetName: 'Ether',
    rpcUrls: [
      'https://apis.zkxwallet.com/invictus/base',
      'https://base-rpc.publicnode.com',
      'https://mainnet.base.org'
    ],
    selectedRpcUrl: 'https://apis.zkxwallet.com/invictus/base',
    rpcNoStateOverride: false,
    chainId: 8453n,
    explorerUrl: 'https://basescan.org',
    erc4337: {
      enabled: true,
      hasPaymaster: true,
      hasBundlerSupport: true,
      bundlers: [PIMLICO, BICONOMY],
      defaultBundler: PIMLICO,
      increasePreVerGas: 5
    },
    // ZKX contracts deployed 2026-07-24 — see Ethereum note (hasRelayer stays
    // false until the ZKX relayer flips it via /v2/config/networks).
    isSAEnabled: true,
    areContractsDeployed: true,
    hasRelayer: false,
    platformId: 'base',
    nativeAssetId: 'ethereum',
    hasSingleton: true,
    isOptimistic: true,
    features: [],
    feeOptions: {
      is1559: true,
      minBaseFeeEqualToLastBlock: true
    },
    predefined: true,
    disableEstimateGas: true,
    wrappedAddr: '0x4200000000000000000000000000000000000006'
  },
  {
    name: 'Scroll',
    nativeAssetSymbol: 'ETH',
    has7702: false,
    nativeAssetName: 'Ether',
    rpcUrls: [
      'https://apis.zkxwallet.com/invictus/scroll',
      'https://rpc.scroll.io',
      'https://scroll-rpc.publicnode.com'
    ],
    selectedRpcUrl: 'https://apis.zkxwallet.com/invictus/scroll',
    rpcNoStateOverride: false,
    chainId: 534352n,
    explorerUrl: 'https://scrollscan.com',
    erc4337: {
      enabled: false,
      hasPaymaster: true,
      hasBundlerSupport: true
    },
    // ZKX contracts NOT deployed here — see Polygon note / §2.6.
    isSAEnabled: false,
    areContractsDeployed: false,
    hasRelayer: false,
    platformId: 'scroll',
    nativeAssetId: 'ethereum',
    hasSingleton: true,
    features: [],
    feeOptions: { is1559: false },
    predefined: true,
    disableEstimateGas: true,
    wrappedAddr: '0x5300000000000000000000000000000000000004'
  },
  {
    name: 'BNB Smart Chain',
    nativeAssetSymbol: 'BNB',
    has7702: true,
    nativeAssetName: 'Binance Coin',
    rpcUrls: [
      'https://apis.zkxwallet.com/invictus/binance-smart-chain',
      'https://bsc-rpc.publicnode.com',
      'https://bsc-dataseed.bnbchain.org'
    ],
    selectedRpcUrl: 'https://apis.zkxwallet.com/invictus/binance-smart-chain',
    rpcNoStateOverride: false,
    chainId: 56n,
    explorerUrl: 'https://bscscan.com/',
    erc4337: {
      enabled: false,
      hasPaymaster: true,
      hasBundlerSupport: true,
      bundlers: [PIMLICO, BICONOMY],
      defaultBundler: PIMLICO
    },
    // ZKX contracts deployed 2026-07-24 — see Ethereum note (hasRelayer stays
    // false until the ZKX relayer flips it via /v2/config/networks).
    isSAEnabled: true,
    areContractsDeployed: true,
    hasRelayer: false,
    platformId: 'binance-smart-chain',
    nativeAssetId: 'binancecoin',
    hasSingleton: true,
    features: [],
    feeOptions: { is1559: true },
    predefined: true,
    disableEstimateGas: true,
    wrappedAddr: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c'
  },
  {
    // ZKX AmbireAccount + Factory deployed 2026-07-24 via EIP-2470 (no 7702 on
    // this chain — v2 smart accounts get a NEW address here). See
    // docs/transactions.md and docs/gas-tank-frontend-integration.md §2.5/§5.
    areContractsDeployed: true,
    chainId: 369n,
    erc4337: { enabled: false, hasPaymaster: false, hasBundlerSupport: false },
    explorerUrl: 'https://ipfs.scan.pulsechain.com',
    features: [
      {
        id: 'saSupport',
        level: 'warning',
        msg: "This network supports Smart Accounts and ZKX Wallet's contracts are deployed, but there is no EIP-7702 here: a Smart Account gets a NEW address. Gas Tank top-ups and spending go through the ZKX relayer, which the backend enables per chain.",
        title: 'Smart Accounts supported (new address)'
      },
      {
        id: 'simulation',
        level: 'warning',
        msg: 'Transaction simulation, one of our security features that predicts the outcome of a transaction before it is broadcast to the blockchain, is not fully functioning on this chain. The reasons might be network or RPC limitations. Try choosing a different RPC.',
        title: 'Transaction simulation is partially supported'
      },
      {
        id: 'prices',
        level: 'success',
        msg: 'We pull token price information in real-time using third-party providers.',
        title: 'Token prices'
      }
    ],
    feeOptions: { is1559: false },
    flagged: false,
    has7702: false,
    hasRelayer: false,
    hasSingleton: true,
    iconUrls: [],
    isOptimistic: false,
    isSAEnabled: true,
    name: 'PulseChain',
    nativeAssetId: 'pulsechain',
    nativeAssetName: 'Pulse',
    nativeAssetSymbol: 'PLS',
    platformId: 'pulsechain',
    predefined: true,
    rpcNoStateOverride: false,
    // Primary is kept as LibertySwap's RPC because the PulseChain swap flow (Permit2)
    // depends on it; the rest are automatic failover targets, all verified to answer
    // eth_chainId + state-override eth_call. Dead endpoints (evex.cloud, owlracle,
    // hairylabs, gigatheminter) were removed so failover doesn't waste hops on them.
    // gigatheminter went behind an nginx 403 for non-browser clients (verified
    // 2026-08-20, also with browser headers), which made every failover hop and
    // every rebroadcast to it a guaranteed miss.
    rpcUrls: [
      'https://rpc.libertyswap.finance',
      'https://pulsechain-rpc.publicnode.com',
      'https://rpc.pulsechainrpc.com',
      'https://rpc.pulsechain.com',
      'https://rpc.pulsechainstats.com',
      'https://rpc-pulsechain.g4mm4.io'
    ],
    selectedRpcUrl: 'https://rpc.libertyswap.finance',
    // WPLS. Required for a native PLS Gas Tank top-up: a bare native transfer
    // emits no Transfer event, so `getTransferRequestParams` wraps first and
    // sends WPLS instead. Without this the top-up cannot be built at all.
    wrappedAddr: '0xA1077a294dDE1B09bB078844df40758a5D0f9a27'
  },
  {
    name: 'Mantle',
    nativeAssetSymbol: 'MNT',
    has7702: false,
    nativeAssetName: 'Mantle',
    rpcUrls: [
      'https://apis.zkxwallet.com/invictus/mantle',
      'https://mantle-rpc.publicnode.com',
      'https://rpc.mantle.xyz'
    ],
    selectedRpcUrl: 'https://apis.zkxwallet.com/invictus/mantle',
    rpcNoStateOverride: false,
    chainId: 5000n,
    explorerUrl: 'https://mantlescan.xyz/',
    erc4337: {
      enabled: true,
      hasPaymaster: true,
      hasBundlerSupport: true
    },
    // ZKX contracts NOT deployed here — see Polygon note / §2.6.
    isSAEnabled: false,
    areContractsDeployed: false,
    hasRelayer: false,
    platformId: 'mantle',
    nativeAssetId: 'mantle',
    hasSingleton: true,
    isOptimistic: true,
    features: [],
    feeOptions: {
      is1559: true,
      minBaseFeeEqualToLastBlock: true
    },
    predefined: true,
    disableEstimateGas: true,
    wrappedAddr: '0x78c1b0C915c4FAA5FffA6CAbf0219DA63d7f4cb8'
  },
  {
    // ZKX contracts NOT deployed here — see Polygon note / §2.6.
    areContractsDeployed: false,
    chainId: 4663n,
    erc4337: { enabled: true, hasPaymaster: true, hasBundlerSupport: true },
    explorerUrl: 'https://robinhoodchain.blockscout.com',
    features: [
      {
        id: 'simulation',
        level: 'warning',
        msg: 'Transaction simulation, one of our security features that predicts the outcome of a transaction before it is broadcast to the blockchain, is not fully functioning on this chain. The reasons might be network or RPC limitations. Try choosing a different RPC.',
        title: 'Transaction simulation is partially supported'
      },
      {
        id: 'prices',
        level: 'success',
        msg: 'We pull token price information in real-time using third-party providers.',
        title: 'Token prices'
      }
    ],
    feeOptions: { is1559: true },
    flagged: false,
    has7702: false,
    hasRelayer: false,
    hasSingleton: true,
    iconUrls: [],
    isOptimistic: false,
    isSAEnabled: false,
    name: 'Robinhood Chain',
    nativeAssetId: 'ethereum',
    nativeAssetName: 'Ether',
    nativeAssetSymbol: 'ETH',
    platformId: 'robinhood-chain',
    predefined: true,
    rpcNoStateOverride: false,
    // The QuikNode endpoint that used to sit here is upstream Ambire's key, not
    // ours: it answers 401 UNAUTHORIZED for every call. It went unnoticed because
    // the relayer ships 4663 with `predefinedConfigVersion: 1` > the 0 assumed for
    // stored predefined networks, so the merge overrode `selectedRpcUrl` with the
    // public RPC before anything used it. That only holds while the relayer answers
    // within its 5s budget - on a fresh install whose merge times out, the seeded
    // value IS what the wallet dials, so a dead URL here bricks the chain. Keep the
    // public RPC as the seeded default.
    rpcUrls: ['https://rpc.mainnet.chain.robinhood.com'],
    selectedRpcUrl: 'https://rpc.mainnet.chain.robinhood.com',
    // Canonical WETH9 on 4663, verified on-chain 2026-09-18: symbol/name WETH,
    // decimals 18, and the EIP-1967 implementation behind this proxy
    // (0xc6b81b429797e0f555440b70cd99e032d7ae947e) does expose deposit() /
    // withdraw(uint256), so the gas-tank native top-up path in
    // `libs/transfer/userRequest.ts` (which encodes a WETH deposit) is real here.
    wrappedAddr: '0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73'
  },
  {
    // Added for LibertySwap USDC-MONAD (CCTP domain 15). liberty-swap points at
    // its own QuikNode key; we use Monad's public RPC instead.
    // ZKX contracts NOT deployed here — see Polygon note / §2.6.
    areContractsDeployed: false,
    chainId: 143n,
    erc4337: { enabled: false, hasPaymaster: false, hasBundlerSupport: false },
    explorerUrl: 'https://monadscan.com',
    features: [
      {
        id: 'simulation',
        level: 'warning',
        msg: 'Transaction simulation, one of our security features that predicts the outcome of a transaction before it is broadcast to the blockchain, is not fully functioning on this chain. The reasons might be network or RPC limitations. Try choosing a different RPC.',
        title: 'Transaction simulation is partially supported'
      },
      {
        id: 'prices',
        level: 'success',
        msg: 'We pull token price information in real-time using third-party providers.',
        title: 'Token prices'
      }
    ],
    feeOptions: { is1559: true },
    flagged: false,
    has7702: false,
    hasRelayer: false,
    hasSingleton: true,
    iconUrls: [],
    isOptimistic: false,
    isSAEnabled: false,
    name: 'Monad',
    nativeAssetId: 'monad',
    nativeAssetName: 'Monad',
    nativeAssetSymbol: 'MON',
    platformId: 'monad',
    predefined: true,
    rpcNoStateOverride: false,
    rpcUrls: ['https://rpc.monad.xyz'],
    selectedRpcUrl: 'https://rpc.monad.xyz'
  },
  {
    // Arc (Circle). Seeded HIDDEN: `disabled: true` keeps it out of
    // `NetworksController.enabledNetworks` until the user turns it on from the
    // network list. Note this only reaches FRESH installs - `#load()` seeds
    // `predefinedNetworks` exclusively when storage is empty, so existing
    // installs learn about 5042 only through the relayer's
    // `GET /v2/config/networks` (ship it there with `disabledByDefault: true`).
    //
    // Arc's native gas token is USDC with 18 decimals, not 6 - do not "fix"
    // the decimals anywhere against the ERC-20 USDC on other chains.
    //
    // ZKX contracts NOT deployed here - see Polygon note / 2.6.
    areContractsDeployed: false,
    chainId: 5042n,
    disabled: true,
    erc4337: { enabled: false, hasPaymaster: false, hasBundlerSupport: false },
    explorerUrl: 'https://explorer.arc.io',
    features: [
      {
        id: 'simulation',
        level: 'warning',
        msg: 'Transaction simulation, one of our security features that predicts the outcome of a transaction before it is broadcast to the blockchain, is not fully functioning on this chain. The reasons might be network or RPC limitations. Try choosing a different RPC.',
        title: 'Transaction simulation is partially supported'
      },
      {
        id: 'prices',
        level: 'success',
        msg: 'We pull token price information in real-time using third-party providers.',
        title: 'Token prices'
      }
    ],
    feeOptions: { is1559: true },
    flagged: false,
    has7702: false,
    hasRelayer: false,
    hasSingleton: true,
    iconUrls: [],
    isOptimistic: false,
    isSAEnabled: false,
    name: 'Arc',
    nativeAssetId: 'usd-coin',
    nativeAssetName: 'USD Coin',
    nativeAssetSymbol: 'USDC',
    platformId: 'arc',
    predefined: true,
    rpcNoStateOverride: false,
    rpcUrls: ['https://rpc.mainnet.arc.io'],
    selectedRpcUrl: 'https://rpc.mainnet.arc.io'
    // No `wrappedAddr` ON PURPOSE. Arc has no wrapped-native token: the native
    // USDC balance is exposed directly through an ERC-20 predeploy at
    // 0x3600000000000000000000000000000000000000, which is a VIEW of the same
    // balance, not a WETH9 wrapper - it has no deposit()/withdraw(). Putting it
    // here would hand `libs/transfer/userRequest.ts` an address it would encode
    // a WETH `deposit()` against. Leaving it undefined makes that path return
    // null, which is the correct outcome. Measured 2026-09-18: the predeploy
    // reports decimals() = 6 while the native balance is scaled to 18 (same
    // account, eth_getBalance / balanceOf differ by exactly 1e12), so the
    // predeploy must never be treated as 1:1 with the native amount either.
  }
]
export { networks }

export const ARBITRUM_CHAIN_ID = 42161n
export const SCROLL_CHAIN_ID = 534352n
